"use client";

import { analyzeIdeaAction } from "@/app/actions/analyze-idea";
import { submitContactForm } from "@/app/actions/send-email";
import { auth } from "@/lib/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useEffect } from "react";

export default function WebMCPInitializer() {
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Chrome 150+ exposes `document.modelContext`; older 146-149 previews used
        // `navigator.modelContext`, which is deprecated and will be removed.
        let modelContext = (document as any).modelContext || (navigator as any).modelContext;

        if (!modelContext || typeof modelContext.registerTool !== "function") {
            const registeredTools = new Map();
            const polyfillContext = {
                registerTool(tool: any, options?: any) {
                    registeredTools.set(tool.name, tool);
                    return Promise.resolve();
                },
                getTools() {
                    return Array.from(registeredTools.values());
                },
                executeTool(name: string, args: any) {
                    const tool = registeredTools.get(name);
                    if (tool && typeof tool.execute === 'function') {
                        return tool.execute(args);
                    }
                    throw new Error(`Tool ${name} not found or not executable.`);
                }
            };
            try {
                Object.defineProperty(Document.prototype, 'modelContext', {
                    get() {
                        return polyfillContext;
                    },
                    configurable: true,
                    enumerable: true
                });
                if (typeof Navigator !== "undefined") {
                    Object.defineProperty(Navigator.prototype, 'modelContext', {
                        get() {
                            return polyfillContext;
                        },
                        configurable: true,
                        enumerable: true
                    });
                }
                modelContext = polyfillContext;
                console.log("WebMCP: Polyfilled modelContext on Document and Navigator prototypes.");
            } catch (e) {
                console.warn("WebMCP: Failed to set polyfill on Document/Navigator prototypes, falling back to direct assignment", e);
                try {
                    (document as any).modelContext = polyfillContext;
                    (navigator as any).modelContext = polyfillContext;
                } catch (err) {}
                modelContext = polyfillContext;
            }
        }

        const controller = new AbortController();
        const registeredNames: string[] = [];

        // 1. Startup Idea Analyzer
        const validateStartupIdeaTool = {
            name: "validate-startup-idea-tool",
            description: "Validates a startup or product idea by checking its market feasibility, estimating development timeline and cost, and identifying key technical risks.",
            inputSchema: {
                type: "object",
                properties: {
                    idea: {
                        type: "string",
                        description: "Detailed description of the startup or app idea to analyze."
                    }
                },
                required: ["idea"]
            },
            execute: async (args: { idea: string }) => {
                const result = await analyzeIdeaAction(args.idea);
                return { success: true, data: result };
            }
        };

        // 2. Submit Inquiry (Contact Form)
        const submitInquiryTool = {
            name: "submit-inquiry-tool",
            description: "Submits a client partnership inquiry, job application, or general inquiry directly to the DharmaTech team.",
            inputSchema: {
                type: "object",
                properties: {
                    name: {
                        type: "string",
                        description: "The inquirer's full name"
                    },
                    email: {
                        type: "string",
                        description: "The inquirer's contact email address"
                    },
                    purpose: {
                        type: "string",
                        description: "The primary purpose of your inquiry: 'Project Partnership', 'Careers / Job Application', or 'General Inquiry'"
                    },
                    message: {
                        type: "string",
                        description: "Details about the project partnership, goals, timeline, budget, or cover letter information for careers."
                    },
                    resumeLink: {
                        type: "string",
                        description: "Link to your resume or portfolio (Only required for Careers / Job Application)."
                    },
                    linkedinProfile: {
                        type: "string",
                        description: "LinkedIn profile or personal portfolio URL (Optional, but recommended for Careers / Job Application)."
                    }
                },
                required: ["name", "email", "purpose", "message"]
            },
            execute: async (args: {
                name: string;
                email: string;
                purpose: string;
                message: string;
                resumeLink?: string;
                linkedinProfile?: string;
            }) => {
                const result = await submitContactForm({
                    name: args.name,
                    email: args.email,
                    purpose: args.purpose,
                    message: args.message,
                    resumeLink: args.resumeLink || "",
                    linkedinProfile: args.linkedinProfile || "",
                    honeypot: "",
                    formLoadTime: Date.now() - 5000,
                });
                return result;
            }
        };

        // 3. Sign Up
        const signUpTool = {
            name: "sign-up-tool",
            description: "Register a new user account with an email and password.",
            inputSchema: {
                type: "object",
                properties: {
                    email: {
                        type: "string",
                        description: "The email address to register"
                    },
                    password: {
                        type: "string",
                        description: "The password to register (minimum 6 characters)"
                    }
                },
                required: ["email", "password"]
            },
            execute: async (args: { email: string; pass: string }) => {
                await createUserWithEmailAndPassword(auth, args.email, args.pass);
                return { success: true, message: "Signed up successfully" };
            }
        };

        const tools = [validateStartupIdeaTool, submitInquiryTool, signUpTool];

        // Register tools concurrently matching the template structure
        Promise.all(
            tools.map(async (tool) => {
                try {
                    // Correct spec signature: modelContext.registerTool(tool, options)
                    await modelContext.registerTool(tool, { signal: controller.signal });
                    registeredNames.push(tool.name);
                } catch (error: any) {
                    if (error.name === "InvalidStateError" || error.message?.includes("Duplicate") || error.message?.includes("already registered")) {
                        console.log(`WebMCP: Tool "${tool.name}" is already registered (declaratively or natively).`);
                    } else {
                        console.error(`Failed to register WebMCP tool "${tool.name}":`, error);
                    }
                }
            })
        );

        return () => {
            // Transitional: unregisterTool is removed in Chrome 148+; signal abort handles unregistration.
            // Call both during the transition window for cross-version compatibility.
            for (const name of registeredNames.splice(0).reverse()) {
                try {
                    modelContext.unregisterTool?.(name);
                } catch {
                    // Ignore stale cleanup during route transitions.
                }
            }
            controller.abort();
        };
    }, []);

    return null;
}
